/**
 * @file 场站列表组件
 * @description 展示附近可预约的充电站，支持定位授权、空状态兜底
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Toast } from "@xp-design/mobile";
import BookingStationItem from "../BookingStationItem";
import { NativeByUrlList } from "@/utils/common";
import { findNearByStationV2 } from "@/api/configPage";
import { processOssImage } from "@/utils/image";
import { useConfigPage, LocationInfo } from "@/hooks/useConfigPage/useConfigPage";
import { trackGdp } from "@/utils/gio-report";
import { useUserContext } from "../../../../routes/home/userContext";
import { getReportVipData, entry } from "../../../../utils";
import { getVipType } from "../../utils/bookingUtils";
import "./index.less";

/**
 * 位置信息接口 - 重新导出供外部使用
 */
export type { LocationInfo };

/**
 * 场站列表配置接口
 */
export interface StationListConfig {
  radius?: number;
  limitNum?: number;
  isLimit?: boolean;
  emptyImageUrl?: string;
  jumpUrlMap?: any;
}

/**
 * 设备概览接口
 */
export interface EquipmentOverview {
  type: string;
  typeName: string;
  chargePowerType: number;
  freeNums: number;
  nums: number;
}

/**
 * 充电站数据接口
 */
export interface StationItemData {
  stationId: number;
  stationName: string;
  stationAddr: string;
  distance: number;
  stationEquipOverViewList: EquipmentOverview[];
  stationAvailabilityDesc?: string;
  stationLabelList?: any[];
  stationBillingInfo?: any;
  stationActivityInfoV5?: any;
}


/**
 * StationList 组件 Props 接口
 */
export interface StationListProps {
  config: StationListConfig;
  /** 组件 ID，用于埋点 */
  componentCode?: string;
  /** 是否有预约机会，没有时展示兜底图片 */
  hasBookingOpportunity?: boolean;
  onStationClick?: (station: StationItemData) => void;
  onViewMoreClick?: () => void;
  onAuthLocationClick?: () => void;
  /** 渲染预约按钮 */
  renderButton?: (station: StationItemData) => React.ReactNode;
}

/**
 * 场站列表组件
 * 展示附近可预约的充电站列表
 */
const StationList: React.FC<StationListProps> = ({
  config,
  componentCode = "",
  hasBookingOpportunity = true,
  onStationClick,
  onViewMoreClick,
  onAuthLocationClick,
  renderButton,
}) => {
  const {
    radius = 10000, // 默认 10km
    limitNum = 1,   // 默认只显示 1 个
    emptyImageUrl = "",
    jumpUrlMap = {},
  } = config;

  // 从上层获取定位和配置信息
  const { location: contextLocation, locationStatus, refetchLocation, configPageQuery } = useConfigPage();
  const { levelInfo } = useUserContext();
  
  const isGettingLocation = locationStatus === 'loading';
  const versionId = configPageQuery?.data?.versionCode || "";
  
  // 状态
  const [station, setStation] = useState<StationItemData | null>(null);
  const [loading, setLoading] = useState(false);
  const [isAuthorizingLocation, setIsAuthorizingLocation] = useState(false);
  // 记录上次请求的定位，用于判断是否需要重新请求
  const lastLocationRef = useRef<{ lng: number; lat: number } | null>(null);
  // 记录是否已上报曝光埋点
  const hasTrackedRef = useRef(false);
  // 记录场站请求是否已完成（用于埋点时序控制）
  const stationFetchedRef = useRef(false);

  // 直接使用上层定位，不再维护本地 location 状态
  const location = contextLocation;

  /**
   * 处理授权定位按钮点击
   */
  const handleAuthLocationClick = useCallback(async () => {
    if (onAuthLocationClick) {
      onAuthLocationClick();
    }
    
    if (refetchLocation) {
      setIsAuthorizingLocation(true);
      try {
        const result = await refetchLocation();
        if (result?.isUseDefault) {
          Toast.show({
            content: "获取定位失败，请稍后重试",
            duration: 2000,
          });
        }
      } finally {
        setIsAuthorizingLocation(false);
      }
    }
  }, [onAuthLocationClick, refetchLocation]);

  /**
   * 获取第一个匹配的充电站
   */
  const fetchFirstStation = useCallback(async (lng: number, lat: number) => {
    setLoading(true);
    stationFetchedRef.current = false; // 开始请求，标记未完成

    try {
      const response = await findNearByStationV2({
        longitude: lng,
        latitude: lat,
        sortType: 2,
        pageNo: 1,
        pageSize: limitNum,
        radius: radius,
        currentLon: lng,
        currentLat: lat,
        filterCriteriaCodeList: ['memberBooking'],
        source: 1,
      });

      if (response.code === 200) {
        const stationList = response.data?.result || [];
        setStation(stationList[0] || null);
      } else {
        console.error(`获取充电站列表失败: ${response.code}`);
        setStation(null);
      }
    } catch (error) {
      console.error("fetchFirstStation error:", error);
      Toast.show({
        content: "加载失败",
        duration: 2000,
      });
      setStation(null);
    } finally {
      stationFetchedRef.current = true; // 请求完成，标记已完成
      setLoading(false);
    }
  }, [radius, limitNum]);
  
  // 始终显示查看更多（因为只显示一个站点）
  const showViewMore = true;

  // 是否使用默认定位（需要显示授权提示）
  const isDefaultLocation = location?.isUseDefault;

  // 是否需要显示兜底状态（默认定位 或 正常定位但无场站）
  // 注意：没有预约机会时不显示兜底图片，直接不渲染
  const showFallback = isDefaultLocation || (!loading && location && !station);

  // 曝光埋点 - B036（定位和加载都完成后上报，只上报一次）
  useEffect(() => {
    // 还在定位中，不上报
    if (isGettingLocation) {
      return;
    }
    // 还在加载场站数据，不上报
    if (loading) {
      return;
    }
    // 已经上报过，不重复上报
    if (hasTrackedRef.current) {
      return;
    }
    // levelInfo 未加载，不上报
    if (!levelInfo) {
      return;
    }
    // location 未加载，不上报
    if (!location) {
      return;
    }
    // 非默认定位时，需要等场站请求完成
    if (!location.isUseDefault && !stationFetchedRef.current) {
      return;
    }

    hasTrackedRef.current = true;
    
    // 判断 node_status：
    // 有可推荐场站：有预约机会 且 非默认定位 且 有场站数据
    // 无可推荐场站：有预约机会 但 (默认定位 或 无场站数据)
    // 无预约机会不推荐：没有预约机会
    let nodeStatus: string;
    if (!hasBookingOpportunity) {
      nodeStatus = "无预约机会不推荐";
    } else if (location.isUseDefault || !station) {
      nodeStatus = "无可推荐场站";
    } else {
      nodeStatus = "有可推荐场站";
    }
    
    const vipData = getReportVipData(levelInfo) as { vip_level: string };
    trackGdp("P11222", "B036", {
      versionId,
      ComponentCode: componentCode,
      description: "充电VIP主页_预约组件_曝光",
      var_ch: entry(),
      vip_level: vipData.vip_level,
      node_status: nodeStatus,
      vip_type: getVipType(levelInfo?.memberTypeSeg, levelInfo?.member),
      total_count: levelInfo?.monthlyBookingLimitMap?.[levelInfo?.level] ?? 0,
      remain_times: (levelInfo?.monthlyBookingLimitMap?.[levelInfo?.level] ?? 0) - (levelInfo?.currentMonthBookingTimes ?? 0),
    });
  }, [isGettingLocation, loading, levelInfo, location, station, versionId, componentCode, hasBookingOpportunity]);

  // 定位成功后加载充电站（定位变化时重新请求）
  useEffect(() => {
    if (!location || location.isUseDefault) {
      return;
    }
    
    // 检查定位是否变化
    const lastLoc = lastLocationRef.current;
    const isSameLocation = lastLoc && 
      lastLoc.lng === location.lng && 
      lastLoc.lat === location.lat;
    
    if (isSameLocation) {
      return; // 定位没变，不重新请求
    }
    
    // 更新记录并请求
    lastLocationRef.current = { lng: location.lng, lat: location.lat };
    fetchFirstStation(location.lng, location.lat);
  }, [location, fetchFirstStation]);

  /**
   * 处理查看更多点击
   * 优先使用外部回调，否则使用配置的跳转链接
   */
  const handleViewMoreClick = () => {
    if (onViewMoreClick) {
      onViewMoreClick();
    } else if (jumpUrlMap) {
      NativeByUrlList(jumpUrlMap);
    }
  };

  /**
   * 渲染充电站列表项
   * @param stationData - 充电站数据
   * @returns 充电站卡片 JSX
   */
  const renderStationItem = (stationData: StationItemData) => {
    const handleItemClick = () => {
      if (onStationClick) {
        onStationClick(stationData);
      }
    };

    return (
      <div key={stationData.stationId} className="station-list-item">
        <BookingStationItem
          station={stationData}
          distance={stationData.distance}
          disabled={!!stationData.stationAvailabilityDesc}
          onClick={handleItemClick}
          renderButton={
            renderButton ? () => renderButton(stationData) : undefined
          }
        />
      </div>
    );
  };


  // 定位中时显示 loading
  if (isGettingLocation) {
    return (
      <div className="station-list-container">
        <div className="station-list-content">
          <div className="loading-container">
            <div className="loading-spinner" />
            <span>正在获取位置...</span>
          </div>
        </div>
      </div>
    );
  }

  /**
   * 处理兜底图点击
   * 跳转到充电站列表页
   */
  const handleFallbackClick = () => {
    if (jumpUrlMap) {
      NativeByUrlList(jumpUrlMap);
    }
  };

  // 没有预约机会时，不渲染任何内容
  if (!hasBookingOpportunity) {
    return null;
  }

  // 默认定位 或 正常定位但无场站时，显示兜底图片
  if (showFallback) {
    return (
      <div className="station-list-container">
        {/* 授权定位提示栏 - 仅在有预约机会且是默认定位时显示 */}
        {hasBookingOpportunity && isDefaultLocation && (
          <div className="location-auth-tip">
            <span className="tip-text">授权定位查看更匹配充电站</span>
            <div 
              className="auth-btn" 
              onClick={handleAuthLocationClick}
            >
              {isAuthorizingLocation ? "定位中..." : "授权定位"}
            </div>
          </div>
        )}
        
        {/* 兜底图片 */}
        {emptyImageUrl && (
          <div className="station-list-content">
            <div className="fallback-container" onClick={handleFallbackClick}>
              <img
                className="fallback-image"
                src={processOssImage(emptyImageUrl)}
                alt=""
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="station-list-container">
      {/* 充电站 */}
      <div className="station-list-content">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner" />
            <span>加载中...</span>
          </div>
        ) : (
          <>
            {/* 显示场站 */}
            {station && renderStationItem(station)}

            {/* 查看更多场站入口 */}
            {showViewMore && station && (
              <div className="view-more-container" onClick={handleViewMoreClick}>
                <span className="view-more-text">查看更多场站</span>
                <img
                  className="view-more-icon"
                  src={processOssImage(
                    "https://s.xiaopeng.com/xp-charge/react-native/pile/more_icon_v2.png"
                  )}
                  alt=""
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StationList;
